import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChatCircleDots, PaperPlaneTilt, Sparkle } from '@phosphor-icons/react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * AI Tutor floating chat modal.
 * Uses /api/learn/v2/tutor (Emergent LLM key). Lives on the Learn page.
 */
const AITutorChat = ({ lessonId = null, onClose }) => {
  const [messages, setMessages] = useState([
    { role: 'tutor', text: "Hi! I'm your TeenVest AI Tutor. Ask me anything about investing — I explain things in plain English." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setBusy(true);
    try {
      const res = await api.post('/learn/v2/tutor', { lesson_id: lessonId, question: q });
      setMessages((m) => [...m, { role: 'tutor', text: res.data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'tutor', text: "I couldn't reach the tutor. Try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="ai-tutor-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg h-[80vh] md:h-[70vh] bg-zinc-950 border border-white/10 md:rounded-2xl flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-cyan-400 flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-black" />
            </div>
            <div>
              <p className="text-white font-unbounded font-bold text-sm">AI Tutor</p>
              <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · powered by GPT-5
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1" data-testid="ai-tutor-close">
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-gold text-black font-medium'
                  : 'bg-white/[0.06] text-zinc-100 border border-white/5'
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-white/[0.06] rounded-2xl px-4 py-2.5 text-sm text-zinc-400 flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask about a concept, quiz question, or definition..."
            disabled={busy}
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-gold/50"
            data-testid="ai-tutor-input"
          />
          <button
            onClick={send}
            disabled={busy || !input.trim()}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-cyan-400 flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
            data-testid="ai-tutor-send"
          >
            <PaperPlaneTilt size={16} weight="fill" className="text-black" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AITutorChat;
